import { ref } from "vue";
import { decodeJwtPayload } from "@/helper/jwt";

export type DraftKind =
  | "object-edit"
  | "object-create"
  | "custom-field-create"
  | "schema-edit"
  | "schema-create"
  | "schema-entry";

export type DraftData = Record<string, unknown>;

export interface Draft<T extends DraftData = DraftData> {
  version: 1;
  kind: DraftKind;
  /** What the draft belongs to: the object id to edit, the parent id to create in (or `root`), … */
  scope: string;
  label: string;
  updatedAt: string;
  data: T;
}

export const DRAFT_KIND_TITLES: Record<DraftKind, string> = {
  "object-edit": "Edit item",
  "object-create": "New item",
  "custom-field-create": "New custom field",
  "schema-edit": "Edit schema",
  "schema-create": "New schema",
  "schema-entry": "Schema entry",
};

const KEY_PREFIX = "draft.";

/** Bumped on every change, so computed lists of drafts re-read the localStorage. */
export const draftsRevision = ref(0);

/** Drafts are namespaced by the user of the access token, so a login as someone else does not show foreign drafts. */
function currentUser(): string {
  try {
    const token = window.localStorage.getItem("accessToken");
    const payload = token ? decodeJwtPayload(token) : null;
    const subject = payload?.sub;
    if (typeof subject === "string" || typeof subject === "number") return String(subject);
  } catch {
    // no storage, no user
  }
  return "anonymous";
}

export function draftKey(kind: DraftKind, scope: string, user: string = currentUser()): string {
  return `${KEY_PREFIX}${user}.${kind}.${scope}`;
}

function isDraft(value: unknown): value is Draft {
  if (typeof value !== "object" || value === null) return false;
  const draft = value as Partial<Draft>;
  return (
    draft.version === 1 &&
    typeof draft.kind === "string" &&
    typeof draft.scope === "string" &&
    typeof draft.label === "string" &&
    typeof draft.updatedAt === "string" &&
    typeof draft.data === "object" &&
    draft.data !== null
  );
}

function parseDraft(raw: string | null): Draft | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function loadDraft(kind: DraftKind, scope: string): Draft | null {
  try {
    return parseDraft(window.localStorage.getItem(draftKey(kind, scope)));
  } catch {
    return null;
  }
}

/** Returns `false` when the draft could not be stored (quota exceeded, storage blocked). */
export function saveDraft(kind: DraftKind, scope: string, label: string, data: DraftData): boolean {
  const draft: Draft = { version: 1, kind, scope, label, updatedAt: new Date().toISOString(), data };
  try {
    window.localStorage.setItem(draftKey(kind, scope), JSON.stringify(draft));
  } catch (error) {
    console.warn("Could not save draft", kind, scope, error);
    return false;
  }
  draftsRevision.value++;
  return true;
}

export function deleteDraft(kind: DraftKind, scope: string): void {
  try {
    window.localStorage.removeItem(draftKey(kind, scope));
  } catch {
    // nothing stored, nothing to delete
  }
  draftsRevision.value++;
}

/** All drafts of the current user, newest first. */
export function listDrafts(): Draft[] {
  const prefix = `${KEY_PREFIX}${currentUser()}.`;
  const drafts: Draft[] = [];
  try {
    for (let index = 0; index < window.localStorage.length; index++) {
      const key = window.localStorage.key(index);
      if (key === null || !key.startsWith(prefix)) continue;
      const draft = parseDraft(window.localStorage.getItem(key));
      if (draft !== null) drafts.push(draft);
    }
  } catch {
    return [];
  }
  return drafts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.values(value as DraftData).every(isEmptyValue);
  return false;
}

/** Names of the fields that hold something, for the "continue this draft?" prompt and the Drafts page. */
export function summarizeDraft(draft: Draft): string[] {
  return Object.entries(draft.data)
    .filter(([, value]) => !isEmptyValue(value))
    .map(([key, value]) => (Array.isArray(value) ? `${key} (${value.length})` : key));
}

/**
 * The entries of `values` that differ from `defaults`.
 * `OpenApiForm` reports every field once any of them changes, so this is what keeps a draft to what the user actually touched.
 */
export function changedFields(values: DraftData, defaults: DraftData): DraftData {
  return Object.fromEntries(
    Object.entries(values).filter(([key, value]) => {
      if (isEmptyValue(value) && isEmptyValue(defaults[key])) return false;
      return JSON.stringify(value) !== JSON.stringify(defaults[key]);
    })
  );
}

export function isSecretFieldType(fieldType: string | null | undefined): boolean {
  return fieldType === "password";
}

/** Drops the entries of `values` whose key is listed in `secretKeys`, e.g. values of password fields. */
export function withoutKeys<T extends Record<string, unknown>>(values: T, secretKeys: Iterable<string>): Partial<T> {
  const secrets = new Set(secretKeys);
  return Object.fromEntries(Object.entries(values).filter(([key]) => !secrets.has(key))) as Partial<T>;
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key.startsWith(KEY_PREFIX)) draftsRevision.value++;
  });
}
